import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import PostPreview from "@/components/PostPreview";
import AgentGraph from "@/components/AgentGraph";
import api, { formatApiError } from "@/lib/api";
import { ChevronDown, ChevronRight } from "lucide-react";

function TraceRow({ trace }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="lk-border rounded-xl" data-testid={`trace-row-${trace.agent_name}`}>
            <button
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between p-4 text-left"
            >
                <div className="flex items-center gap-3">
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <div>
                        <div className="font-heading text-lg capitalize">{trace.agent_name} Agent</div>
                        <div className="text-xs text-[var(--lk-muted)] font-mono">{trace.provider}/{trace.model}</div>
                    </div>
                </div>
                <div className="flex items-center gap-6">
                    <div className="text-right">
                        <div className="lk-label">Latency</div>
                        <div className="font-mono text-sm text-[var(--lk-primary)]">{Math.round(trace.latency_ms)} ms</div>
                    </div>
                    <div className="text-right">
                        <div className="lk-label">Tokens</div>
                        <div className="font-mono text-sm text-[var(--lk-primary)]">{trace.tokens}</div>
                    </div>
                    <div className={`text-xs font-mono ${trace.status === "success" ? "text-emerald-400" : "text-red-400"}`}>{trace.status}</div>
                </div>
            </button>
            {open ? (
                <div className="border-t border-[var(--lk-border)] p-4 grid md:grid-cols-2 gap-4">
                    <div>
                        <div className="lk-label mb-2">Prompt</div>
                        <pre className="font-mono text-xs whitespace-pre-wrap text-[var(--lk-muted)] max-h-80 overflow-auto lk-border rounded-lg p-3">{trace.prompt}</pre>
                    </div>
                    <div>
                        <div className="lk-label mb-2">Response</div>
                        <pre className="font-mono text-xs whitespace-pre-wrap text-[var(--lk-text)] max-h-80 overflow-auto lk-border rounded-lg p-3">{trace.response || "(empty)"}</pre>
                        {trace.error ? <div className="text-red-400 text-xs mt-2">Error: {trace.error}</div> : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default function TraceViewer() {
    const { id } = useParams();
    const [gen, setGen] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/history/${id}`);
                setGen(data);
            } catch (e) {
                setErr(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) return (
        <div className="min-h-screen"><Navbar /><div className="max-w-6xl mx-auto px-6 py-10 text-[var(--lk-muted)]" data-testid="trace-loading">Loading trace…</div></div>
    );

    if (err || !gen) return (
        <div className="min-h-screen"><Navbar />
            <div className="max-w-6xl mx-auto px-6 py-10">
                <div className="text-red-400" data-testid="trace-error">{err || "Not found"}</div>
                <Link to="/history" className="mt-4 inline-block text-[var(--lk-primary)] underline">← Back to history</Link>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-6xl mx-auto px-6 py-10">
                <Link to="/history" className="text-xs text-[var(--lk-muted)] hover:text-[var(--lk-primary)]" data-testid="trace-back-link">← Back to history</Link>
                <div className="lk-label mt-4">Generation</div>
                <h1 className="font-heading text-3xl sm:text-4xl mt-2 tracking-tight truncate">{gen.prompt}</h1>
                <div className="text-xs text-[var(--lk-muted)] mt-2 font-mono">{gen.model}</div>

                <div className="mt-10">
                    <AgentGraph traces={gen.prompt_logs} />
                </div>

                <div className="mt-10 grid md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                        <PostPreview generation={gen} />
                    </div>
                    <div className="lk-border lk-surface rounded-xl p-6">
                        <div className="lk-label">Metrics</div>
                        <div className="mt-3 space-y-3 text-sm">
                            <div className="flex justify-between"><span className="text-[var(--lk-muted)]">Total latency</span><span className="font-mono text-[var(--lk-primary)]">{Math.round(gen.total_latency_ms)} ms</span></div>
                            <div className="flex justify-between"><span className="text-[var(--lk-muted)]">Total tokens</span><span className="font-mono text-[var(--lk-primary)]">{gen.total_tokens}</span></div>
                            <div className="flex justify-between"><span className="text-[var(--lk-muted)]">Quality score</span><span className="font-mono text-[var(--lk-primary)]">{gen.quality_score != null ? Math.round(gen.quality_score) : "—"}</span></div>
                            <div className="flex justify-between"><span className="text-[var(--lk-muted)]">Status</span><span className="font-mono">{gen.status}</span></div>
                        </div>
                        {gen.quality_report ? (
                            <div className="mt-6">
                                <div className="lk-label mb-2">Quality report</div>
                                <div className="text-xs text-[var(--lk-muted)] whitespace-pre-wrap">{gen.quality_report}</div>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="mt-12">
                    <div className="lk-label mb-4">Per-agent traces</div>
                    <div className="space-y-3">
                        {(gen.prompt_logs || []).map((t) => <TraceRow key={t.id} trace={t} />)}
                    </div>
                </div>
            </div>
        </div>
    );
}
